import React from 'react';
import { ThumbsUp, Heart, Share2 } from 'lucide-react';

// Render Live Indicator
export const renderLiveIndicator = (event, size = 'small') => {
  if (!event.is_live || !event.stream_url) return null;

  const badgeSize = size === 'large' ? 'large' : 'small';
  const dotSize = size === 'large' ? '12px' : '8px';
  const fontSize = size === 'large' ? '0.9rem' : '0.75rem';
  const padding = size === 'large' ? '0.5rem 1rem' : '0.35rem 0.75rem';

  return (
    <a
      href={event.stream_url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: padding,
        background: 'rgba(0, 255, 0, 0.2)',
        border: '2px solid rgba(0, 255, 0, 0.6)',
        borderRadius: '20px',
        color: '#00ff00',
        fontSize: fontSize,
        fontWeight: '700',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        animation: 'pulse 2s infinite'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(0, 255, 0, 0.3)';
        e.currentTarget.style.transform = 'scale(1.05)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(0, 255, 0, 0.2)';
        e.currentTarget.style.transform = 'scale(1)';
      }}
    >
      <div style={{
        width: dotSize,
        height: dotSize,
        borderRadius: '50%',
        background: '#00ff00',
        animation: 'flash 1s infinite',
        boxShadow: '0 0 10px rgba(0, 255, 0, 0.8)'
      }} />
      <span>LIVE NOW</span>
      <style>{`
        @keyframes flash {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(0, 255, 0, 0.4); }
          50% { box-shadow: 0 0 0 8px rgba(0, 255, 0, 0); }
        }
      `}</style>
    </a>
  );
};

// Render Over Badge
export const renderOverBadge = (event, size = 'small', theme = 'gray') => {
  if (!event.is_over) return null;

  const fontSize = size === 'large' ? '0.9rem' : '0.75rem';
  const padding = size === 'large' ? '0.5rem 1rem' : '0.35rem 0.75rem';

  const colors = theme === 'red' 
    ? { bg: 'rgba(255, 0, 0, 0.2)', border: 'rgba(255, 0, 0, 0.6)', text: '#ff0000' }
    : { bg: 'rgba(128, 128, 128, 0.2)', border: 'rgba(128, 128, 128, 0.4)', text: 'rgba(255, 255, 255, 0.5)' };

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.4rem',
        padding: padding,
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: '20px',
        color: colors.text,
        fontSize: fontSize,
        fontWeight: '600'
      }}
    >
      OVER
    </span>
  );
};

// Render Reaction Buttons
export const renderReactionButtons = (event, size, userReactions, handlers) => {
  const reactions = userReactions[event.id] || {};
  const buttonSize = size === 'large' 
    ? { padding: '0.5rem 1rem', fontSize: '0.9rem', iconSize: 18 } 
    : { padding: '0.3rem 0.6rem', fontSize: '0.75rem', iconSize: 14 };
  
  return (
    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
      {/* Like Button */}
      <button
        onClick={() => {
          handlers.loadUserReactions(event.id);
          handlers.handleLike(event.id);
        }}
        style={{
          padding: buttonSize.padding,
          background: reactions.has_liked ? 'rgba(0, 255, 255, 0.3)' : 'rgba(0, 255, 255, 0.1)',
          border: `1px solid ${reactions.has_liked ? 'rgba(0, 255, 255, 0.6)' : 'rgba(0, 255, 255, 0.3)'}`,
          borderRadius: '6px',
          color: '#00ffff',
          fontSize: buttonSize.fontSize,
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.3rem',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(0, 255, 255, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = reactions.has_liked ? 'rgba(0, 255, 255, 0.3)' : 'rgba(0, 255, 255, 0.1)';
        }}
      >
        <ThumbsUp size={buttonSize.iconSize} fill={reactions.has_liked ? '#00ffff' : 'none'} />
        <span>{event.likes || 0}</span>
      </button>

      {/* Love Button */}
      <button
        onClick={() => {
          handlers.loadUserReactions(event.id);
          handlers.handleLove(event.id);
        }}
        style={{
          padding: buttonSize.padding,
          background: reactions.has_loved ? 'rgba(255, 0, 255, 0.3)' : 'rgba(255, 0, 255, 0.1)',
          border: `1px solid ${reactions.has_loved ? 'rgba(255, 0, 255, 0.6)' : 'rgba(255, 0, 255, 0.3)'}`,
          borderRadius: '6px',
          color: '#ff00ff',
          fontSize: buttonSize.fontSize,
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.3rem',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 0, 255, 0.3)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = reactions.has_loved ? 'rgba(255, 0, 255, 0.3)' : 'rgba(255, 0, 255, 0.1)';
        }}
      >
        <Heart size={buttonSize.iconSize} fill={reactions.has_loved ? '#ff00ff' : 'none'} />
        <span>{event.loves || 0}</span>
      </button>

      {/* Share Button */}
      <button
        onClick={() => handlers.handleShareClick(event)}
        style={{
          padding: buttonSize.padding,
          background: 'rgba(255, 165, 0, 0.1)',
          border: '1px solid rgba(255, 165, 0, 0.3)',
          borderRadius: '6px',
          color: '#ffa500',
          fontSize: buttonSize.fontSize,
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.3rem',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 165, 0, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 165, 0, 0.1)';
        }}
      >
        <Share2 size={buttonSize.iconSize} />
        <span>{event.shares || 0}</span>
      </button>

      {/* Share to The Wall Button */}
      {handlers.handleWallShareClick && (
        <button
          onClick={() => handlers.handleWallShareClick(event)}
          title="Share to The Wall"
          data-testid={`event-wall-share-${event.id}`}
          style={{
            padding: buttonSize.padding,
            background: 'rgba(6, 182, 212, 0.1)',
            border: '1px solid rgba(6, 182, 212, 0.3)',
            borderRadius: '6px',
            color: '#22d3ee',
            fontSize: buttonSize.fontSize,
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.3rem',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(6, 182, 212, 0.25)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(6, 182, 212, 0.1)';
          }}
        >
          <Share2 size={buttonSize.iconSize} />
          <span>Wall</span>
        </button>
      )}
    </div>
  );
};
